import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import TemplateImportModal from './TemplateImportModal';
import HTMLCSSEditor from './HTMLCSSEditor';
import TemplateEditor from './TemplateEditor';

const { FiPlus, FiSearch, FiFilter, FiFileText, FiCode, FiDownload, FiTrash2, FiEye, FiEdit, FiCopy, FiPrinter } = FiIcons;

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorType, setEditorType] = useState('html-css');
  const [showQuickStart, setShowQuickStart] = useState(true);

  // Load templates from localStorage
  useEffect(() => {
    const storedTemplates = JSON.parse(localStorage.getItem('exam_templates') || '[]');
    setTemplates(storedTemplates);
    setFilteredTemplates(storedTemplates);
  }, []);

  // Filter templates based on search term and filter type
  useEffect(() => {
    let filtered = templates;
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(template => template.type === filterType);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(term)
      );
    }
    
    setFilteredTemplates(filtered);
  }, [templates, searchTerm, filterType]);

  const handleDeleteTemplate = (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      const updatedTemplates = templates.filter(template => template.id !== id);
      setTemplates(updatedTemplates);
      localStorage.setItem('exam_templates', JSON.stringify(updatedTemplates));
    }
  };

  const handleDuplicateTemplate = (template) => {
    const newTemplate = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedTemplates = [...templates, newTemplate];
    setTemplates(updatedTemplates);
    localStorage.setItem('exam_templates', JSON.stringify(updatedTemplates));
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setEditorType(template.type || 'html-css');
    setShowEditor(true);
  };

  const handleCreateTemplate = (type) => {
    setEditingTemplate(null);
    setEditorType(type);
    setShowEditor(true);
  };

  const handleBackFromEditor = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    
    // Reload templates to see any changes
    const storedTemplates = JSON.parse(localStorage.getItem('exam_templates') || '[]');
    setTemplates(storedTemplates);
    setFilteredTemplates(storedTemplates);
  };

  const handleViewPDF = (template) => {
    try {
      // Open the HTML content in a new window for printing
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        alert("Please allow pop-ups to view the PDF");
        return;
      }
      
      // Write content to the new window with proper doctype
      printWindow.document.write('<!DOCTYPE html>');
      printWindow.document.write(template.finalHTML || template.htmlContent);
      
      // Ensure the document is fully loaded before printing
      printWindow.document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      });
      
      // Close only happens after print dialog is closed
      printWindow.document.close();
    } catch (error) {
      console.error("Error generating PDF view:", error);
      alert("There was an error generating the PDF view. Please try again.");
    }
  };

  // If showing the editor component
  if (showEditor) {
    if (editorType === 'html-css') {
      return <HTMLCSSEditor onBack={handleBackFromEditor} template={editingTemplate} />;
    } else if (editorType === 'visual-builder') {
      return <TemplateEditor onBack={handleBackFromEditor} template={editingTemplate} />;
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="ml-72 p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Exam Templates</h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} />
              <span>Import</span>
            </button>
          </div>
        </div>

        {/* Quick Start Templates */}
        {showQuickStart && (
          <div className="bg-white rounded-lg shadow-sm mb-8 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Quick Start Templates</h2>
              <button
                onClick={() => setShowQuickStart(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => handleCreateTemplate('visual-builder')}
                >
                  <div className="flex items-start">
                    <div className="bg-blue-500 rounded-lg p-3 text-white mr-4">
                      <SafeIcon icon={FiFileText} className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Visual Builder</h3>
                      <p className="text-gray-600 mb-4">
                        Create exam templates using a drag-and-drop interface with blocks and placeholders. Easy to use with no coding required.
                      </p>
                      <div className="flex items-center text-blue-600 font-medium">
                        <span>Start building</span>
                        <SafeIcon icon={FiPlus} className="ml-1" />
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => handleCreateTemplate('html-css')}
                >
                  <div className="flex items-start">
                    <div className="bg-purple-500 rounded-lg p-3 text-white mr-4">
                      <SafeIcon icon={FiCode} className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Advanced Template</h3>
                      <p className="text-gray-600 mb-4">
                        Full HTML/CSS editor for maximum customization and control. Perfect for advanced users who need pixel-perfect templates.
                      </p>
                      <div className="flex items-center text-purple-600 font-medium">
                        <span>Start coding</span>
                        <SafeIcon icon={FiPlus} className="ml-1" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        )}

        {/* Search and filter */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="relative flex-grow">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="relative">
            <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">All Types</option>
              <option value="html-css">Advanced (HTML/CSS)</option>
              <option value="visual-builder">Visual Builder</option>
            </select>
          </div>

          <button
            onClick={() => setShowQuickStart(prev => !prev)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showQuickStart ? 'Hide Quick Start' : 'Show Quick Start'}
          </button>
        </div>

        {/* Templates grid */}
        {filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200"
              >
                <div className="h-40 bg-gray-100 border-b border-gray-200 flex items-center justify-center overflow-hidden">
                  {template.htmlContent ? (
                    <div
                      className="w-full h-full transform scale-50 pointer-events-none"
                      style={{ overflow: 'hidden' }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: template.finalHTML || template.htmlContent }}
                        style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}
                      />
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      <SafeIcon icon={template.type === 'html-css' ? FiCode : FiFileText} className="text-4xl" />
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-800 mr-2">{template.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      template.type === 'html-css' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {template.type === 'html-css' ? 'Advanced' : 'Visual Builder'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-4">
                    {template.templateType === 'cover' ? 'Cover Page' : 'Body Page'} • 
                    Last updated {new Date(template.updatedAt).toLocaleDateString()}
                  </p>

                  <div className="flex justify-between items-center">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button
                        onClick={() => handleDuplicateTemplate(template)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        title="Duplicate"
                      >
                        <SafeIcon icon={FiCopy} />
                      </button>
                      <button
                        onClick={() => handleViewPDF(template)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                        title="View as PDF"
                      >
                        <SafeIcon icon={FiPrinter} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Open
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <SafeIcon icon={FiFileText} className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">No templates found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterType !== 'all' 
                ? 'Try changing your search or filter criteria'
                : 'Create your first template to get started'}
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => handleCreateTemplate('visual-builder')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="inline mr-1" />
                Visual Builder
              </button>
              <button
                onClick={() => handleCreateTemplate('html-css')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="inline mr-1" />
                Advanced Template
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Template Import Modal */}
      {showImportModal && (
        <TemplateImportModal 
          onClose={() => setShowImportModal(false)} 
          onImport={(importedTemplates) => {
            const updatedTemplates = [...templates, ...importedTemplates];
            setTemplates(updatedTemplates);
            localStorage.setItem('exam_templates', JSON.stringify(updatedTemplates));
            setShowImportModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Templates;